import React, { useState, useEffect } from 'react';
import { Bell, X, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const STORAGE_KEY = 'sauce_push_banner_dismissed_at';
const REASK_DAYS = 30;

const PushPermissionBanner = () => {
  const { currentUser } = useAuth();
  const {
    supported,
    iosNeedsInstall,
    permission,
    subscribed,
    registering,
    subscribe,
  } = usePushNotifications(currentUser);

  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    // Hide for staff (admins/barbers know about notifications already and
    // may have permission UX in their dashboards). Show for everyone else,
    // including anonymous visitors.
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'barber')) return;
    if (!supported) return;
    if (subscribed) return;
    if (permission === 'denied') return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const ms = Date.now() - new Date(dismissedAt).getTime();
      if (ms < REASK_DAYS * 86400000) return;
    }
    // Delay banner so it doesn't appear instantly on first paint
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [currentUser, supported, subscribed, permission]);

  const handleEnable = async () => {
    if (iosNeedsInstall) {
      setShowIosHelp(true);
      return;
    }
    const res = await subscribe();
    if (res.ok) {
      toast.success('Notifications enabled 🔔');
      setVisible(false);
    } else if (res.reason === 'permission_denied') {
      toast.error('Notifications blocked. Enable them in your browser settings.');
      dismiss();
    } else {
      toast.error('Could not enable notifications');
    }
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 bg-gradient-to-br from-purple-900 to-zinc-900 border border-purple-500/40 rounded-xl shadow-2xl p-4"
        data-testid="push-permission-banner"
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-zinc-400 hover:text-white"
          aria-label="dismiss"
          data-testid="push-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-4">
          <div className="bg-lime-500 text-black rounded-full p-2 flex-shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-bold text-base mb-1">
              Get reminders & deals
            </h4>
            <p className="text-zinc-300 text-sm mb-3">
              Appointment reminders, exclusive promo drops, and member-only deals — straight to your phone.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleEnable}
                disabled={registering}
                size="sm"
                className="bg-lime-500 hover:bg-lime-600 text-black font-semibold"
                data-testid="push-banner-enable"
              >
                {registering ? 'Enabling…' : 'Allow notifications'}
              </Button>
              <Button
                onClick={dismiss}
                size="sm"
                variant="ghost"
                className="text-zinc-300 hover:text-white"
                data-testid="push-banner-not-now"
              >
                Not now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* iPhone install instructions modal */}
      {showIosHelp && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-md w-full p-5 relative">
            <button
              onClick={() => setShowIosHelp(false)}
              className="absolute top-3 right-3 text-zinc-400 hover:text-white"
              aria-label="close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-6 h-6 text-lime-400" />
              <h3 className="text-white text-lg font-bold">Add Sauce Sity to your home screen</h3>
            </div>
            <p className="text-zinc-300 text-sm mb-4">
              Apple requires Safari to install the app first before notifications work. It only takes 10 seconds:
            </p>
            <ol className="text-zinc-200 text-sm space-y-3 mb-4">
              <li className="flex items-start gap-2">
                <span className="bg-lime-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>Tap the <Share className="inline w-4 h-4 align-middle text-blue-400" /> Share icon at the bottom of Safari</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-lime-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong> <Plus className="inline w-4 h-4 align-middle" /></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-lime-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Tap <strong>&quot;Add&quot;</strong> in the top right</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-lime-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">4</span>
                <span>Open Sauce Sity from your home screen and tap <strong>Allow notifications</strong> when asked</span>
              </li>
            </ol>
            <Button
              onClick={() => setShowIosHelp(false)}
              className="w-full bg-lime-500 hover:bg-lime-600 text-black font-semibold"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PushPermissionBanner;
